import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { Search, X, Package, MapPin, Phone, Mail, Calendar } from 'lucide-react';

const S = {
  heading: { fontSize: 26, fontWeight: 300, letterSpacing: '0.06em', color: '#f5f0e8', marginBottom: 4 },
  sub: { fontSize: 13, color: 'rgba(245,240,232,0.35)', marginBottom: 28, letterSpacing: '0.03em' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  section: { background: '#13110d', border: '1px solid rgba(186,130,69,0.1)', borderRadius: 10, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    fontSize: 9, letterSpacing: '0.35em', color: 'rgba(245,240,232,0.25)',
    textTransform: 'uppercase', padding: '12px 20px', textAlign: 'left',
    borderBottom: '1px solid rgba(186,130,69,0.06)', whiteSpace: 'nowrap',
  },
  td: {
    padding: '14px 20px', borderBottom: '1px solid rgba(186,130,69,0.05)',
    fontSize: 14, color: '#f5f0e8', verticalAlign: 'middle',
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
    zIndex: 100,
  },
  drawer: {
    background: '#13110d', width: '100%', maxWidth: 500,
    height: '100vh', overflow: 'auto',
    borderLeft: '1px solid rgba(186,130,69,0.15)',
    fontFamily: "'Cormorant Garamond', Georgia, serif",
  },
};

const statusOptions = ['pending', 'processing', 'shipped', 'delivered', 'cancelled'];

const statusColors = {
  pending:    { bg: 'rgba(234,179,8,0.12)',   text: '#fde047', border: 'rgba(234,179,8,0.3)' },
  processing: { bg: 'rgba(59,130,246,0.12)',  text: '#93c5fd', border: 'rgba(59,130,246,0.3)' },
  shipped:    { bg: 'rgba(139,92,246,0.12)',  text: '#c4b5fd', border: 'rgba(139,92,246,0.3)' },
  paid:       { bg: 'rgba(34,197,94,0.12)',   text: '#86efac', border: 'rgba(34,197,94,0.3)' },
  delivered:  { bg: 'rgba(34,197,94,0.12)',   text: '#86efac', border: 'rgba(34,197,94,0.3)' },
  cancelled:  { bg: 'rgba(239,68,68,0.12)',   text: '#fca5a5', border: 'rgba(239,68,68,0.3)' },
};

const StatusBadge = ({ status }) => {
  const c = statusColors[status] || statusColors.pending;
  return (
    <span style={{
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
      borderRadius: 20, padding: '3px 10px', fontSize: 11,
      letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap',
    }}>{status}</span>
  );
};

const AdminOrders = () => {
  const [orders, setOrders]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState('');
  const [statusFilter, setStatusFilter]   = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [saved, setSaved]                 = useState(false);

  const fetchOrders = async () => {
    const { data } = await supabase
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false });
    setOrders(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchOrders(); }, []);

  // ─── Open drawer — items come straight from order.items (jsonb) ───
  const openOrder = (order) => {
    setSelectedOrder(order);
    setTrackingNumber(order.tracking_number || '');
    setSaved(false);
  };

  const updateStatus = async (orderId, newStatus) => {
    setUpdatingStatus(true);
    const updates = { status: newStatus };
    if (newStatus === 'delivered') updates.delivered_at = new Date().toISOString();
    await supabase.from('orders').update(updates).eq('id', orderId);
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, ...updates } : o));
    setSelectedOrder(prev => ({ ...prev, ...updates }));
    setUpdatingStatus(false);
  };

  const saveTracking = async () => {
    if (!selectedOrder) return;
    setSaved(false);
    try {
      // Call backend — updates DB + sends "Order Shipped" email to customer
      const res = await fetch(
        'https://i-kept-all-the-code-in-github-and-now-i.onrender.com/api/assign-tracking',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderId: selectedOrder.id,
            trackingNumber,
            courierName: '',
          }),
        }
      );
      const result = await res.json();
      if (!result.success) throw new Error(result.message);
    } catch (err) {
      console.error('Tracking save error:', err);
      // Fallback: direct Supabase update even if email fails
      await supabase.from('orders').update({ tracking_number: trackingNumber, status: 'shipped' }).eq('id', selectedOrder.id);
    }
    setOrders(prev => prev.map(o => o.id === selectedOrder.id ? { ...o, tracking_number: trackingNumber, status: 'shipped' } : o));
    setSelectedOrder(prev => ({ ...prev, tracking_number: trackingNumber, status: 'shipped' }));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const filtered = orders.filter(o => {
    const matchSearch = (
      String(o.id).includes(search) ||
      (o.order_id || '').toLowerCase().includes(search.toLowerCase()) ||
      (o.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (o.phone || '').includes(search)
    );
    const matchStatus = statusFilter === 'all' || o.status === statusFilter;
    return matchSearch && matchStatus;
  });

  // ─── Parse items from jsonb column ───────────────────────────────
  const getItems = (order) => {
    if (!order) return [];
    try {
      if (Array.isArray(order.items)) return order.items;
      if (typeof order.items === 'string') return JSON.parse(order.items);
    } catch {}
    return [];
  };

  return (
    <div style={{ color: '#f5f0e8' }}>
      <div style={S.heading}>Orders</div>
      <div style={S.sub}>Manage all customer orders · {orders.length} total</div>

      {/* ── Toolbar ── */}
      <div style={S.toolbar}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(245,240,232,0.25)' }} />
            <input
              style={{
                background: '#13110d', border: '1px solid rgba(186,130,69,0.15)',
                borderRadius: 6, padding: '10px 14px 10px 34px', color: '#f5f0e8',
                fontSize: 14, outline: 'none', width: 260,
                fontFamily: "'Cormorant Garamond', Georgia, serif",
              }}
              placeholder="Order ID, email or phone..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {['all', ...statusOptions].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} style={{
              background: statusFilter === s ? 'rgba(186,130,69,0.15)' : '#13110d',
              border: `1px solid ${statusFilter === s ? 'rgba(186,130,69,0.4)' : 'rgba(186,130,69,0.1)'}`,
              borderRadius: 6, padding: '8px 14px',
              color: statusFilter === s ? '#ba8245' : 'rgba(245,240,232,0.4)',
              fontSize: 12, cursor: 'pointer', letterSpacing: '0.05em',
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              textTransform: 'capitalize',
            }}>{s}</button>
          ))}
        </div>
      </div>

      {/* ── Table ── */}
      <div style={S.section}>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {['Order', 'Customer', 'Items', 'Amount', 'Status', 'Payment', 'Date', 'Action'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', color: 'rgba(245,240,232,0.2)' }}>Loading...</td></tr>
              ) : filtered.map(order => {
                const items = getItems(order);
                return (
                  <tr
                    key={order.id}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(186,130,69,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ ...S.td, color: '#ba8245', fontWeight: 500 }}>
                      <div>#{order.id}</div>
                      {order.order_id && <div style={{ fontSize: 10, color: 'rgba(186,130,69,0.45)', marginTop: 2 }}>{order.order_id}</div>}
                    </td>
                    <td style={S.td}>
                      <div style={{ fontSize: 13 }}>{order.customer_name || order.email || 'Guest'}</div>
                      <div style={{ fontSize: 11, color: 'rgba(245,240,232,0.3)' }}>{order.phone || '—'}</div>
                    </td>
                    <td style={{ ...S.td, fontSize: 12, color: 'rgba(245,240,232,0.5)' }}>
                      {items.length > 0 ? (
                        <span style={{ background: 'rgba(186,130,69,0.08)', border: '1px solid rgba(186,130,69,0.15)', borderRadius: 12, padding: '2px 8px', color: '#ba8245', fontSize: 11 }}>
                          {items.length} item{items.length !== 1 ? 's' : ''}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ ...S.td, fontWeight: 500 }}>₹{parseFloat(order.total_amount || 0).toLocaleString('en-IN')}</td>
                    <td style={S.td}><StatusBadge status={order.status} /></td>
                    <td style={{ ...S.td, fontSize: 12, color: 'rgba(245,240,232,0.5)' }}>
                      {order.razorpay_payment_id
                        ? <span style={{ color: '#86efac' }}>✓ Paid</span>
                        : order.payment_method || '—'}
                    </td>
                    <td style={{ ...S.td, fontSize: 12, color: 'rgba(245,240,232,0.4)' }}>
                      {new Date(order.created_at).toLocaleDateString('en-IN')}
                    </td>
                    <td style={S.td}>
                      <button
                        onClick={() => openOrder(order)}
                        style={{
                          background: 'rgba(186,130,69,0.08)', border: '1px solid rgba(186,130,69,0.2)',
                          borderRadius: 5, padding: '6px 12px', color: '#ba8245',
                          fontSize: 12, cursor: 'pointer',
                          fontFamily: "'Cormorant Garamond', Georgia, serif",
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', color: 'rgba(245,240,232,0.2)' }}>No orders found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Order Drawer ── */}
      {selectedOrder && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && setSelectedOrder(null)}>
          <div style={S.drawer}>

            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(186,130,69,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 11, letterSpacing: '0.3em', color: 'rgba(245,240,232,0.3)', textTransform: 'uppercase', marginBottom: 4 }}>Order</div>
                <div style={{ fontSize: 20, color: '#ba8245', fontWeight: 300 }}>#{selectedOrder.id}</div>
                {selectedOrder.order_id && (
                  <div style={{ fontSize: 11, color: 'rgba(186,130,69,0.4)', marginTop: 2 }}>{selectedOrder.order_id}</div>
                )}
              </div>
              <button onClick={() => setSelectedOrder(null)} style={{ background: 'none', border: 'none', color: 'rgba(245,240,232,0.4)', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '24px' }}>

              {/* Status Update */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 10, letterSpacing: '0.35em', color: 'rgba(245,240,232,0.35)', textTransform: 'uppercase', marginBottom: 10 }}>Update Status</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {statusOptions.map(s => (
                    <button
                      key={s}
                      disabled={updatingStatus}
                      onClick={() => updateStatus(selectedOrder.id, s)}
                      style={{
                        padding: '8px 14px', borderRadius: 20, cursor: 'pointer',
                        fontSize: 12, letterSpacing: '0.05em', textTransform: 'capitalize',
                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                        transition: 'all 0.15s',
                        ...(selectedOrder.status === s
                          ? { background: '#ba8245', color: '#0f0d0a', border: '1px solid #ba8245', fontWeight: 600 }
                          : { background: 'transparent', color: 'rgba(245,240,232,0.5)', border: '1px solid rgba(186,130,69,0.15)' }
                        ),
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tracking Number */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 10, letterSpacing: '0.35em', color: 'rgba(245,240,232,0.35)', textTransform: 'uppercase', marginBottom: 10 }}>Tracking Number</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={trackingNumber}
                    onChange={e => { setTrackingNumber(e.target.value); setSaved(false); }}
                    placeholder="Enter tracking number..."
                    style={{
                      flex: 1, background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(186,130,69,0.15)', borderRadius: 6,
                      padding: '10px 14px', color: '#f5f0e8', fontSize: 14,
                      outline: 'none', fontFamily: "'Cormorant Garamond', Georgia, serif",
                    }}
                  />
                  <button onClick={saveTracking} style={{
                    background: saved ? 'rgba(34,197,94,0.8)' : '#ba8245',
                    border: 'none', borderRadius: 6,
                    padding: '10px 16px', color: '#0f0d0a', cursor: 'pointer', fontSize: 13,
                    fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600,
                    transition: 'background 0.3s', minWidth: 60,
                  }}>
                    {saved ? '✓' : 'Save'}
                  </button>
                </div>
              </div>

              {/* Customer Info */}
              <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '16px', marginBottom: 20, border: '1px solid rgba(186,130,69,0.08)' }}>
                <div style={{ fontSize: 10, letterSpacing: '0.35em', color: 'rgba(245,240,232,0.25)', textTransform: 'uppercase', marginBottom: 12 }}>Customer</div>
                {[
                  { icon: Mail,    value: selectedOrder.email },
                  { icon: Phone,   value: selectedOrder.phone },
                  { icon: MapPin,  value: [selectedOrder.address, selectedOrder.city, selectedOrder.state, selectedOrder.pincode].filter(Boolean).join(', ') || selectedOrder.address },
                  { icon: Calendar,value: new Date(selectedOrder.created_at).toLocaleString('en-IN') },
                ].filter(row => row.value).map(({ icon: Icon, value }) => (
                  <div key={value} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
                    <Icon size={13} style={{ color: 'rgba(186,130,69,0.5)', flexShrink: 0, marginTop: 2 }} />
                    <span style={{ fontSize: 13, color: 'rgba(245,240,232,0.65)', lineHeight: 1.5 }}>{value}</span>
                  </div>
                ))}
                {selectedOrder.razorpay_payment_id && (
                  <div style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(34,197,94,0.08)', borderRadius: 5, border: '1px solid rgba(34,197,94,0.15)', fontSize: 11, color: '#86efac' }}>
                    ✓ Payment ID: {selectedOrder.razorpay_payment_id}
                  </div>
                )}
              </div>

              {/* ── Order Items (from jsonb) ── */}
              <div>
                <div style={{ fontSize: 10, letterSpacing: '0.35em', color: 'rgba(245,240,232,0.25)', textTransform: 'uppercase', marginBottom: 14 }}>
                  Items Ordered
                </div>

                {getItems(selectedOrder).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px 0', color: 'rgba(245,240,232,0.2)', fontSize: 13 }}>
                    No item details stored for this order
                  </div>
                ) : (
                  getItems(selectedOrder).map((item, i) => (
                    <div key={i} style={{
                      display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center',
                      background: 'rgba(255,255,255,0.02)', borderRadius: 8,
                      padding: '10px 12px', border: '1px solid rgba(186,130,69,0.06)',
                    }}>
                      {/* Image */}
                      {(item.image || item.images?.[0] || item.img) ? (
                        <img
                          src={item.image || item.images?.[0] || item.img}
                          alt={item.name}
                          style={{ width: 52, height: 60, objectFit: 'cover', borderRadius: 5, flexShrink: 0 }}
                        />
                      ) : (
                        <div style={{ width: 52, height: 60, background: 'rgba(186,130,69,0.08)', borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Package size={16} style={{ color: 'rgba(186,130,69,0.3)' }} />
                        </div>
                      )}

                      {/* Details */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, color: '#f5f0e8', marginBottom: 4, fontWeight: 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.name || item.product_name || item.title || `Item #${i + 1}`}
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {item.size && (
                            <span style={{ fontSize: 11, color: 'rgba(245,240,232,0.4)', background: 'rgba(255,255,255,0.04)', borderRadius: 4, padding: '2px 6px' }}>
                              Size: {item.size}
                            </span>
                          )}
                          {item.quantity && (
                            <span style={{ fontSize: 11, color: 'rgba(245,240,232,0.4)', background: 'rgba(255,255,255,0.04)', borderRadius: 4, padding: '2px 6px' }}>
                              Qty: {item.quantity}
                            </span>
                          )}
                          {item.color && (
                            <span style={{ fontSize: 11, color: 'rgba(245,240,232,0.4)', background: 'rgba(255,255,255,0.04)', borderRadius: 4, padding: '2px 6px' }}>
                              {item.color}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Price */}
                      <div style={{ color: '#ba8245', fontWeight: 500, fontSize: 14, flexShrink: 0 }}>
                        ₹{parseFloat(item.price || item.total || 0).toLocaleString('en-IN')}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Total */}
              <div style={{ borderTop: '1px solid rgba(186,130,69,0.08)', paddingTop: 16, marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 15, color: 'rgba(245,240,232,0.5)' }}>Total</span>
                <span style={{ fontSize: 22, color: '#ba8245', fontWeight: 300 }}>
                  ₹{parseFloat(selectedOrder.total_amount || 0).toLocaleString('en-IN')}
                </span>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;